import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { setTenantSlug } from '../services/tenantSlugRef';
import { setUserChosenMunicipio } from '../services/userChosenMunicipio';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TenantContext = createContext(null);

/**
 * TenantProvider - Manages municipality context for the application
 * 
 * Features:
 * - Reads municipio_slug from URL path
 * - Validates municipality with backend
 * - Provides municipality data to all children
 * - Handles municipality switching
 */
export const TenantProvider = ({ children }) => {
  const { municipioSlug } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  
  const [currentMunicipio, setCurrentMunicipio] = useState(null);
  const [municipios, setMunicipios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch list of all active municipalities
  const fetchMunicipios = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/municipios`);
      setMunicipios(response.data || []);
      return response.data || [];
    } catch (err) {
      console.error('Error fetching municipalities:', err);
      return [];
    }
  }, []);

  // Fetch specific municipality by slug
  const fetchMunicipio = useCallback(async (slug) => {
    if (!slug) return null;
    
    try {
      const response = await axios.get(`${API_URL}/api/municipios/${slug}`);
      return response.data;
    } catch (err) {
      console.error('Error fetching municipality:', err);
      return null;
    }
  }, []);

  // Find nearest municipality based on coordinates
  const findNearestMunicipio = useCallback(async (lat, lng) => {
    try {
      const response = await axios.get(`${API_URL}/api/municipios/nearest`, {
        params: { lat, lng }
      });
      return response.data;
    } catch (err) {
      console.error('Error finding nearest municipality:', err);
      return null;
    }
  }, []);

  // Switch to a different municipality
  const switchMunicipio = useCallback((slug) => {
    if (!slug) return;
    
    // Get current path without municipality prefix
    const currentPath = location.pathname;
    const pathParts = currentPath.split('/').filter(Boolean);
    
    // If current path starts with a municipality slug, remove it
    const currentMunSlug = municipios.find(m => m.slug === pathParts[0])?.slug;
    let newPath;
    
    if (currentMunSlug) {
      // Replace current municipality with new one
      pathParts[0] = slug;
      newPath = '/' + pathParts.join('/');
    } else {
      // Add municipality to path
      newPath = `/${slug}${currentPath}`;
    }
    
    navigate(newPath);
  }, [location.pathname, municipios, navigate]);

  // Initialize: fetch municipalities and validate current one
  useEffect(() => {
    const initialize = async () => {
      setLoading(true);
      setError(null);
      
      try {
        // Fetch all municipalities
        const allMunicipios = await fetchMunicipios();
        
        // If we have a slug in URL, validate it
        if (municipioSlug) {
          const municipio = await fetchMunicipio(municipioSlug);
          
          if (municipio) {
            setCurrentMunicipio(municipio);
          } else {
            setError('Município não encontrado');
            // Could redirect to a default municipality here if needed
          }
        } else {
          // No municipality in URL - global context or needs selection
          setCurrentMunicipio(null);
        }
      } catch (err) {
        console.error('Tenant initialization error:', err);
        setError('Erro ao carregar dados do município');
      } finally {
        setLoading(false);
      }
    };
    
    initialize();
  }, [municipioSlug, fetchMunicipios, fetchMunicipio]);

  // Sync resolved tenant slug to module-level ref so the axios request
  // interceptor (running outside React) can auto-inject `?municipio_slug=...`
  // for /v2/* calls. Source of truth is React Router, not URL string parsing.
  useEffect(() => {
    setTenantSlug(currentMunicipio?.slug || null);
    // "Stick to last visited city": if the user reached a tenant route
    // explicitly (link, breadcrumb, navigation), persist that as their
    // chosen município so all future legacy redirects respect it.
    if (currentMunicipio?.slug) {
      setUserChosenMunicipio({
        id: currentMunicipio.id,
        slug: currentMunicipio.slug,
        nome: currentMunicipio.nome,
      });
    }
    return () => setTenantSlug(null);
  }, [currentMunicipio]);

  const value = {
    // Current municipality
    currentMunicipio,
    municipioId: currentMunicipio?.id || null,
    municipioSlug: currentMunicipio?.slug || null,
    municipioNome: currentMunicipio?.nome || null,
    
    // All municipalities
    municipios,
    
    // State
    loading,
    error,
    isGlobalContext: !currentMunicipio,
    
    // Actions
    switchMunicipio,
    findNearestMunicipio,
    fetchMunicipios,
    
    // Utility to build paths with municipality prefix
    buildPath: (path) => {
      if (!currentMunicipio) return path;
      return `/${currentMunicipio.slug}${path.startsWith('/') ? path : '/' + path}`;
    }
  };

  return (
    <TenantContext.Provider value={value}>
      {children}
    </TenantContext.Provider>
  );
};

/**
 * Hook to access tenant context
 */
export const useTenant = () => {
  const context = useContext(TenantContext);
  if (!context) {
    throw new Error('useTenant must be used within a TenantProvider');
  }
  return context;
};

/**
 * Component to select municipality
 */
export const MunicipioSelector = ({ className = '', showLabel = true }) => {
  const { municipios, currentMunicipio, switchMunicipio, loading } = useTenant();
  
  if (loading || municipios.length === 0) {
    return null;
  }

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      {showLabel && (
        <span className="text-sm text-muted-foreground">Município:</span>
      )}
      <select
        value={currentMunicipio?.slug || ''}
        onChange={(e) => switchMunicipio(e.target.value)}
        className="px-3 py-1.5 border rounded-lg text-sm bg-white focus:ring-2 focus:ring-[var(--primary)]/20 focus:border-[var(--primary)]"
        data-testid="municipio-selector"
      >
        {!currentMunicipio && <option value="">Selecione...</option>}
        {municipios.map((mun) => (
          <option key={mun.id} value={mun.slug}>
            {mun.nome} - {mun.uf}
          </option>
        ))}
      </select>
    </div>
  );
};

export default TenantContext;
